import { useState, useEffect } from 'react';
import { formatLocalDate, parseLocalDate } from '../../utils/dateFormat';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { Ticket, Coins, Loader2, Trophy, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function RafflesPage() {
  const { api } = useAuth();
  const [raffles, setRaffles] = useState([]);
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRaffle, setSelectedRaffle] = useState(null);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [ticketCount, setTicketCount] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rafflesRes, learnersRes] = await Promise.all([
        api.get('/raffles/'),
        api.get('/learners/'),
      ]);
      // Handle both paginated (Django) and non-paginated responses
      setRaffles(rafflesRes.data?.results || rafflesRes.data || []);
      setLearners(learnersRes.data?.results || learnersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedLearner || !selectedRaffle || !termsAccepted) return;

    const learner = learners.find(l => l.id === selectedLearner);
    const totalCost = selectedRaffle.ticket_cost * ticketCount;
    
    if (learner.points_balance < totalCost) {
      toast.error('Insufficient points');
      return;
    }

    setPurchasing(true);
    try {
      await api.post(`/raffles/${selectedRaffle.id}/entries`, {
        raffle_id: selectedRaffle.id,
        learner_id: selectedLearner,
        ticket_count: ticketCount,
      });
      toast.success(`Purchased ${ticketCount} ticket(s)!`);
      setSelectedRaffle(null);
      setTicketCount(1);
      setTermsAccepted(false);
      await fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to purchase tickets';
      toast.error(message);
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="raffles-page">
      <div>
        <h1 className="font-heading text-2xl font-bold">Raffles</h1>
        <p className="text-muted-foreground">Enter raffles using your points for a chance to win!</p>
      </div>

      {raffles.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <Ticket className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No active raffles</h2>
            <p className="text-muted-foreground">Check back soon for new raffles!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {raffles.map((raffle, index) => {
            const isActive = raffle.active && !raffle.winner_id;
            // raffle.end_date is a DateField (YYYY-MM-DD) — parse in local TZ
            // so the "Ends" label never slips a day back in western timezones.
            const endDate = parseLocalDate(raffle.end_date);
            const isEnded = endDate && endDate < new Date();

            return (
              <Card 
                key={raffle.id} 
                className={`card-marketing ${!isActive ? 'opacity-60' : ''}`}
                data-testid={`raffle-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Trophy className="h-7 w-7 text-primary" />
                    </div>
                    <Badge variant={isActive ? 'default' : 'secondary'}>
                      {raffle.winner_id ? 'Completed' : isEnded ? 'Ended' : 'Active'}
                    </Badge>
                  </div>

                  <h3 className="font-heading text-xl font-bold mb-2">{raffle.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{raffle.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Trophy className="h-4 w-4 text-secondary" />
                      <span className="font-medium">Prize: {raffle.prize}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Coins className="h-4 w-4" />
                      <span>{raffle.ticket_cost} points per ticket</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Ends: {formatLocalDate(raffle.end_date, { month: 'numeric', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Ticket className="h-4 w-4" />
                      <span>{raffle.total_entries} total entries</span>
                    </div>
                  </div>

                  {isActive && (
                    <Button 
                      className="w-full"
                      onClick={() => setSelectedRaffle(raffle)}
                      data-testid={`enter-raffle-${index}`}
                    >
                      Enter Raffle
                    </Button>
                  )}

                  {raffle.winner_id && (
                    <div className="p-3 rounded-lg bg-accent/10 text-center">
                      <p className="text-sm font-medium text-accent">Winner announced!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Purchase Dialog */}
      <Dialog open={!!selectedRaffle} onOpenChange={() => setSelectedRaffle(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter Raffle</DialogTitle>
          </DialogHeader>
          {selectedRaffle && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted/50">
                <h3 className="font-semibold">{selectedRaffle.name}</h3>
                <p className="text-sm text-secondary font-medium mt-1">
                  Prize: {selectedRaffle.prize}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedRaffle.ticket_cost} points per ticket
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Select Learner</label>
                <Select value={selectedLearner} onValueChange={setSelectedLearner}>
                  <SelectTrigger data-testid="raffle-learner-select">
                    <SelectValue placeholder="Choose learner" />
                  </SelectTrigger>
                  <SelectContent>
                    {learners.map(learner => (
                      <SelectItem key={learner.id} value={learner.id}>
                        {learner.first_name} {learner.last_name} ({learner.points_balance || 0} pts)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Number of Tickets</label>
                <Input
                  type="number"
                  min={1}
                  max={selectedRaffle.max_tickets_per_family}
                  value={ticketCount}
                  onChange={(e) => setTicketCount(Math.min(parseInt(e.target.value) || 1, selectedRaffle.max_tickets_per_family))}
                  data-testid="ticket-count-input"
                />
                <p className="text-xs text-muted-foreground">
                  Max {selectedRaffle.max_tickets_per_family} tickets per family
                </p>
              </div>

              <div className="p-4 rounded-xl bg-secondary/10">
                <div className="flex justify-between font-bold">
                  <span>Total Cost</span>
                  <span className="text-secondary">
                    {selectedRaffle.ticket_cost * ticketCount} points
                  </span>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  data-testid="raffle-terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight">
                  I accept the raffle terms: {selectedRaffle.terms}
                </label>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedRaffle(null)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handlePurchase}
                  disabled={!selectedLearner || !termsAccepted || purchasing}
                  data-testid="confirm-purchase-btn"
                >
                  {purchasing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Purchase Tickets
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
